import { Button, Icon, Tag, Tooltip } from '@pypestream/design-system';
import { FC, useState } from 'react';

import type { CanvasAgentNode, CanvasUngroupedNode } from './canvas-view-model';

import './agent-sidebar.scss';

export interface AgentSidebarProps {
  agents: CanvasAgentNode[];
  filterText: string;
  onFilterChange: (value: string) => void;
  hiddenAgentIds: Set<string>;
  onToggleAgent: (id: string) => void;
  onSetVisibility: (ids: string[], visible: boolean) => void;
  /** Real signal from `canvas-edges-model.ts`'s own derivation — an agent id
   *  that appears as a `CanvasEdge`'s source or target, not a guess. */
  routedAgentIds: Set<string>;
  collapsedAgentIds: Set<string>;
  /** Open or close one agent's card (the caret at the row's end). */
  onToggleCollapse: (agentId: string) => void;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  /** Rows in no real group, in row order (`buildUngroupedNodes`). */
  ungroupedNodes: CanvasUngroupedNode[];
  /** `tableStore.showUngroupedNodes` — collapsed by default. */
  ungroupedOpen: boolean;
  onToggleUngrouped: () => void;
  /** Opens the node in the detail panel; absent when that panel is off. */
  onUngroupedClick?: (rowId: string) => void;
  /** Opens "Suggest agents"; absent for a viewer. */
  onSuggestAgents?: () => void;
}

/** Fewer ungrouped nodes than this are quicker to group by hand. */
export const SUGGEST_AGENTS_FROM = 10;

/**
 * Ungrouped rows drawn at a time. A legacy solution arrives with thousands
 * of nodes and no groups; drawing every one as a row stalled the whole
 * canvas (4,200 rows: seconds per re-render).
 */
const UNGROUPED_PAGE = 200;

const matchesText = (text: string, filterText: string): boolean =>
  !filterText.trim() ||
  text.toLowerCase().includes(filterText.trim().toLowerCase());

const matchesFilter = (agent: CanvasAgentNode, filterText: string): boolean =>
  matchesText(agent.title, filterText);

/**
 * Build Stage canvas, Phase 3 build B — the agent list, docked on the left.
 * Visually the prototype's `.cv-filter` (`prostudio-build-flow-layouts.html`):
 * a count header, a compact search, pill bulk actions, and one row per agent
 * under sticky "Core" / "Use-case agents" labels (see `CanvasAgentCategory`
 * for why exactly those two; a Use Case Agent gets no special-casing — see
 * `reference_use_case_agents_category`).
 *
 * The search here and the topbar's "Find an agent…" drive the same filter
 * state (`canvas-view.tsx`): one filter, which dims non-matching cards and
 * rows rather than hiding them. Hiding is the row checkbox's job.
 *
 * "Only routed" reads `routedAgentIds`, computed from `buildCanvasEdges`'s
 * real output — not inferred from anything about the agent itself.
 */
export const AgentSidebar: FC<AgentSidebarProps> = ({
  agents,
  filterText,
  onFilterChange,
  hiddenAgentIds,
  onToggleAgent,
  onSetVisibility,
  routedAgentIds,
  collapsedAgentIds,
  onCollapseAll,
  onExpandAll,
  onToggleCollapse,
  ungroupedNodes,
  ungroupedOpen,
  onToggleUngrouped,
  onUngroupedClick,
  onSuggestAgents,
}) => {
  const [ungroupedLimit, setUngroupedLimit] = useState(UNGROUPED_PAGE);
  const visibleCount = agents.filter((a) => !hiddenAgentIds.has(a.id)).length;
  const core = agents.filter((a) => a.category === 'core');
  const useCase = agents.filter((a) => a.category === 'use-case');
  const allIds = agents.map((a) => a.id);
  const routedIds = agents
    .filter((a) => routedAgentIds.has(a.id))
    .map((a) => a.id);

  const renderSection = (title: string, section: CanvasAgentNode[]) =>
    section.length > 0 && (
      <div className="c-canvas-sidebar__section">
        <div className="c-canvas-sidebar__group-label">
          {title} — {section.length}
        </div>
        {section.map((agent) => {
          const visible = !hiddenAgentIds.has(agent.id);
          const dimmedByFilter = !matchesFilter(agent, filterText);
          return (
            <button
              key={agent.id}
              type="button"
              role="checkbox"
              aria-checked={visible}
              className={[
                'c-canvas-sidebar__row',
                !visible && 'is-off',
                dimmedByFilter && 'c-canvas-sidebar__row--dimmed',
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid="bobcat-canvas-sidebar-agent-checkbox"
              onClick={() => onToggleAgent(agent.id)}
            >
              <span className="c-canvas-sidebar__box" aria-hidden="true">
                {visible && <Icon name="check" size="xsmall" />}
              </span>
              <span
                className="c-canvas-sidebar__dot"
                style={
                  agent.color ? { backgroundColor: agent.color } : undefined
                }
                aria-hidden="true"
              />
              <span className="c-canvas-sidebar__name">{agent.title}</span>
              <span className="c-canvas-sidebar__n">{agent.nodeCount}</span>
              {/* A span, not a button — the row is itself a button. At the
              row's end so the carets line up down the list (the prototype's
              `.cvf-exp`), with the arrows Expand/Collapse all use. */}
              {agent.nodeCount > 0 && (
                <Tooltip
                  placement="right"
                  content={
                    collapsedAgentIds.has(agent.id) ? 'Expand' : 'Collapse'
                  }
                >
                  <span
                    className="c-canvas-sidebar__exp"
                    role="button"
                    tabIndex={0}
                    data-testid="bobcat-canvas-sidebar-collapse"
                    aria-label={`${
                      collapsedAgentIds.has(agent.id) ? 'Expand' : 'Collapse'
                    } ${agent.title}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleCollapse(agent.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleCollapse(agent.id);
                      }
                    }}
                  >
                    <Icon
                      name={
                        collapsedAgentIds.has(agent.id) ? 'expand' : 'collapse'
                      }
                      size="xsmall"
                    />
                  </span>
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>
    );

  // Nodes in no agent live here, not over the canvas: a list pinned to the
  // canvas corner drew on top of the cards. Same one filter, on node names.
  // With a filter, only the rows it matches, rather than every row dimmed.
  const ungroupedMatches = filterText.trim()
    ? ungroupedNodes.filter((node) => matchesText(node.label, filterText))
    : ungroupedNodes;
  const ungroupedShown = ungroupedMatches.slice(0, ungroupedLimit);
  const ungroupedLeft = ungroupedMatches.length - ungroupedShown.length;

  const renderUngrouped = () =>
    ungroupedNodes.length > 0 && (
      <div
        className="c-canvas-sidebar__section"
        data-testid="bobcat-canvas-sidebar-ungrouped"
      >
        <button
          type="button"
          className="c-canvas-sidebar__group-label c-canvas-sidebar__group-toggle"
          aria-expanded={ungroupedOpen}
          data-testid="bobcat-canvas-ungrouped-toggle"
          onClick={onToggleUngrouped}
        >
          <Icon
            name={ungroupedOpen ? 'chevron-down' : 'chevron-right'}
            size="xsmall"
          />
          Ungrouped — {ungroupedNodes.length}
        </button>
        {onSuggestAgents && ungroupedNodes.length >= SUGGEST_AGENTS_FROM && (
          <Button
            className="c-canvas-sidebar__suggest"
            variant="secondary"
            size="small"
            test-id="bobcat-canvas-suggest-agents"
            onClick={onSuggestAgents}
          >
            <Icon slot="prefix" name="stars" size="xsmall" />
            Suggest agents
          </Button>
        )}
        {ungroupedOpen &&
          ungroupedShown.map((node) => {
            const className = [
              'c-canvas-sidebar__row',
              'c-canvas-sidebar__row--node',
              !matchesText(node.label, filterText) &&
                'c-canvas-sidebar__row--dimmed',
            ]
              .filter(Boolean)
              .join(' ');
            const content = (
              <>
                <span className="c-canvas-sidebar__num">
                  {node.nodeNumber ?? '—'}
                </span>
                <span className="c-canvas-sidebar__name">{node.label}</span>
              </>
            );
            return onUngroupedClick ? (
              <button
                key={node.id}
                type="button"
                className={className}
                data-testid="bobcat-canvas-ungrouped-node"
                onClick={() => onUngroupedClick(node.id)}
              >
                {content}
              </button>
            ) : (
              <div
                key={node.id}
                className={className}
                data-testid="bobcat-canvas-ungrouped-node"
              >
                {content}
              </div>
            );
          })}
        {ungroupedOpen && ungroupedLeft > 0 && (
          <button
            type="button"
            className="c-canvas-sidebar__more"
            data-testid="bobcat-canvas-ungrouped-more"
            onClick={() => setUngroupedLimit((n) => n + UNGROUPED_PAGE)}
          >
            Show {Math.min(UNGROUPED_PAGE, ungroupedLeft)} more (
            {ungroupedLeft.toLocaleString()} left)
          </button>
        )}
      </div>
    );

  return (
    <aside
      className="c-canvas-sidebar"
      aria-label="Agents"
      data-testid="bobcat-canvas-sidebar"
    >
      <div className="c-canvas-sidebar__head">
        <div className="c-canvas-sidebar__title">
          <h2>Agents</h2>
          <span className="c-canvas-sidebar__count">
            {visibleCount} of {agents.length} on the canvas
          </span>
        </div>
        <input
          className="c-canvas-sidebar__search"
          type="search"
          placeholder="Filter agents…"
          aria-label="Filter agents"
          value={filterText}
          data-testid="bobcat-canvas-sidebar-filter"
          onChange={(e) => onFilterChange(e.target.value)}
        />
        {/* What is ON the canvas: filter choices, so pills. `minimal` drops
            the tag's status dot — these are not deployment states. */}
        <div className="c-canvas-sidebar__bulk">
          <Tag
            size="small"
            minimal
            interactive
            newVariant="outlined"
            onclick={() => onSetVisibility(allIds, true)}
          >
            All
          </Tag>
          <Tag
            size="small"
            minimal
            interactive
            newVariant="outlined"
            onclick={() => onSetVisibility(allIds, false)}
          >
            None
          </Tag>
          <Tag
            size="small"
            minimal
            interactive
            newVariant="outlined"
            onclick={() => {
              onSetVisibility(allIds, false);
              onSetVisibility(routedIds, true);
            }}
          >
            Only routed
          </Tag>
        </div>
        {/* How much of each card shows: actions, so buttons. */}
        <div className="c-canvas-sidebar__bulk c-canvas-sidebar__bulk--open">
          <Button variant="secondary" size="small" onClick={onExpandAll}>
            <Icon slot="prefix" name="expand" size="xsmall" />
            Expand all
          </Button>
          <Button variant="secondary" size="small" onClick={onCollapseAll}>
            <Icon slot="prefix" name="collapse" size="xsmall" />
            Collapse all
          </Button>
        </div>
      </div>
      <div className="c-canvas-sidebar__list">
        {renderSection('Core', core)}
        {renderSection('Use-case agents', useCase)}
        {renderUngrouped()}
        {agents.length === 0 && (
          <div className="c-canvas-sidebar__empty">No agent groups yet.</div>
        )}
      </div>
    </aside>
  );
};

export { matchesFilter };
